import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleSlash,
  Clock,
  PackageCheck,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/api";
import type { OrderDetail, ProductionItemStatus } from "@/lib/types";
import { ExpeditionDecisionModal, type ExpeditionMode } from "@/components/admin/ExpeditionDecisionModal";

export type ExpeditionItemUpdate = {
  productId: string;
  unit: string;
  cut?: string | null;
  status: "PENDENTE" | "CARREGADO" | "EM_FALTA";
  loadedQuantity?: number;
  reason?: string;
};

/**
 * Conferência de expedição reutilizável: tabela de itens com ações de Carregar /
 * Em falta / editar quantidade. Usada tanto no detalhe do admin quanto na janela
 * dedicada do dashboard de expedição — fonte única de verdade do fluxo.
 */
export function ExpeditionConferenceList({
  order,
  onUpdate,
  collapsible = true,
}: {
  order: OrderDetail;
  onUpdate: (input: ExpeditionItemUpdate) => Promise<void>;
  collapsible?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [decision, setDecision] = useState<{
    item: OrderDetail["expeditionList"][number];
    mode: ExpeditionMode;
  } | null>(null);

  if (order.status === "CRIADO" || order.status === "EM_COMPRA" || order.status === "EM_PRODUCAO") {
    return null;
  }
  const editable = order.status === "EM_EXPEDICAO";
  const list = order.expeditionList ?? [];
  if (list.length === 0) return null;
  const loaded = list.filter((i) => i.status === "CARREGADO").length;
  const missing = list.filter((i) => i.status === "EM_FALTA").length;
  const pending = list.filter((i) => i.status === "PENDENTE").length;

  // Carregar é 1 clique: assume o total produzido (ou o pedido, se não houver decisão de produção).
  const loadFull = async (item: OrderDetail["expeditionList"][number]) => {
    const qty = item.producedQuantity != null ? Number(item.producedQuantity) : Number(item.requestedQuantity);
    try {
      await onUpdate({
        productId: item.productId,
        unit: item.unit,
        cut: item.cut ?? null,
        status: "CARREGADO",
        loadedQuantity: qty,
      });
      toast.success("Item carregado");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível atualizar o item"));
    }
  };

  const submitDecision = async (input: { loadedQuantity: number; reason?: string }) => {
    if (!decision) return;
    const { item, mode } = decision;
    // Editar a quantidade conferida a partir de um item ainda PENDENTE registra a carga
    // e promove o item para CARREGADO (é o que de fato foi expedido).
    const status =
      mode === "EDIT" ? (item.status === "PENDENTE" ? "CARREGADO" : item.status) : mode;
    try {
      await onUpdate({
        productId: item.productId,
        unit: item.unit,
        cut: item.cut ?? null,
        status,
        loadedQuantity: input.loadedQuantity,
        reason:
          mode === "EM_FALTA" ? input.reason : mode === "EDIT" ? item.reason ?? undefined : undefined,
      });
      toast.success(
        mode === "EM_FALTA" ? "Item em falta" : mode === "EDIT" ? "Quantidade atualizada" : "Item carregado",
      );
      setDecision(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível atualizar o item"));
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {collapsible && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0 text-muted-foreground"
              onClick={() => setCollapsed((v) => !v)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expandir conferência de expedição" : "Recolher conferência de expedição"}
              title={collapsed ? "Expandir" : "Recolher"}
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          )}
          <h3 className="flex items-center gap-2 font-display text-base font-semibold">
            <PackageCheck className="h-4 w-4" /> Conferência de expedição
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700">
            {loaded}/{list.length} carregado{list.length === 1 ? "" : "s"}
          </span>
          {missing > 0 && (
            <span className="rounded-full bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700">
              {missing} em falta
            </span>
          )}
          {pending > 0 && editable && (
            <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground">
              {pending} pendente{pending === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
      <div className={cn("overflow-hidden rounded-xl border border-border bg-surface", collapsed && "hidden")}>
        <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "36%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead className="bg-surface-elevated text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Produto</th>
              <th className="px-3 py-2 text-right font-medium">Pedido</th>
              <th className="px-3 py-2 text-right font-medium">Produzido</th>
              <th className="px-3 py-2 text-right font-medium">Carregado</th>
              <th className="px-3 py-2 text-right font-medium">{editable ? "Ações" : "Status"}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((item) => {
              const loaded = item.loadedQuantity == null ? null : Number(item.loadedQuantity);
              const requested = Number(item.requestedQuantity);
              const prod = productionCell(item.productionStatus, item.producedQuantity);
              const load = loadCell(item.status, loaded);
              const loadDiff = loaded == null ? 0 : loaded - requested;
              return (
                <tr key={`${item.productId}-${item.unit}-${item.cut ?? ""}`} className="border-t border-border align-top">
                  {/* Produto */}
                  <td className="px-3 py-2.5">
                    <div className="font-medium leading-tight">{item.productName}</div>
                    {(item.cut || item.isInNatura) && (
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {item.cut && (
                          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                            {item.cut}
                          </span>
                        )}
                        {item.isInNatura && (
                          <span className="rounded-full border border-lime-500/30 bg-lime-500/10 px-2 py-0.5 text-[10px] font-medium text-lime-700">
                            IN NATURA
                          </span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Pedido */}
                  <td className="px-3 py-2.5">
                    <QtyNum value={fmtQty(requested)} unit={item.unit} />
                  </td>

                  {/* Produzido — cor e sub-rótulo destacam quando não veio completo */}
                  <td className="px-3 py-2.5">
                    <QtyNum value={prod.value} unit={item.unit} tone={prod.tone} strike={prod.strike} />
                    {prod.note && (
                      <div className={cn("text-right text-[10px] font-medium uppercase tracking-wide", prod.noteClass)}>
                        {prod.note}
                      </div>
                    )}
                  </td>

                  {/* Carregado */}
                  <td className="px-3 py-2.5">
                    <QtyNum value={load.value} unit={item.unit} tone={load.tone} />
                    {item.status === "CARREGADO" && loadDiff < 0 && (
                      <div className="text-right text-[10px] font-medium uppercase tracking-wide text-amber-700">
                        falta {fmtQty(Math.abs(loadDiff))}
                      </div>
                    )}
                    {item.status === "CARREGADO" && loadDiff > 0 && (
                      <div className="text-right text-[10px] uppercase tracking-wide text-muted-foreground">
                        sobra {fmtQty(loadDiff)}
                      </div>
                    )}
                    {item.status === "EM_FALTA" && item.reason && (
                      <div className="text-right text-[10px] text-muted-foreground">{item.reason}</div>
                    )}
                  </td>

                  {/* Ações (editável) ou status de expedição */}
                  <td className="px-3 py-2.5 text-right">
                    {editable ? (
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Carregar o total produzido"
                          aria-label="Carregar o total produzido"
                          className={cn(
                            "h-8 w-8 p-0 text-emerald-700 hover:bg-emerald-500/10",
                            item.status === "CARREGADO" && "bg-emerald-500/15",
                          )}
                          onClick={() => void loadFull(item)}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Marcar em falta"
                          aria-label="Marcar em falta"
                          className={cn(
                            "h-8 w-8 p-0 text-amber-700 hover:bg-amber-500/10",
                            item.status === "EM_FALTA" && "bg-amber-500/15",
                          )}
                          onClick={() => setDecision({ item, mode: "EM_FALTA" })}
                        >
                          <CircleSlash className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Editar quantidade conferida"
                          aria-label="Editar quantidade conferida"
                          className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted"
                          onClick={() => setDecision({ item, mode: "EDIT" })}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <ExpeditionBadge status={item.status} />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {decision && (
        <ExpeditionDecisionModal
          item={decision.item}
          mode={decision.mode}
          onCancel={() => setDecision(null)}
          onSubmit={submitDecision}
        />
      )}
    </section>
  );
}

/** Quantidade legível: remove zeros à direita; "—" quando ausente. */
function fmtQty(n?: number | string | null): string {
  if (n == null) return "—";
  return Number(n).toFixed(3).replace(/\.?0+$/, "");
}

type QtyTone = "muted" | "strong" | "ok" | "warn" | "bad";

const QTY_TONE: Record<QtyTone, string> = {
  muted: "text-muted-foreground",
  strong: "text-foreground",
  ok: "text-emerald-700",
  warn: "text-amber-700",
  bad: "text-destructive",
};

/** Número grande de quantidade com unidade discreta — o destaque visual da tela. */
function QtyNum({
  value,
  unit,
  tone = "strong",
  strike = false,
}: {
  value: string;
  unit?: string;
  tone?: QtyTone;
  strike?: boolean;
}) {
  return (
    <div className="text-right leading-tight">
      <span className={cn("text-base font-medium tabular-nums", QTY_TONE[tone], strike && "line-through")}>{value}</span>
      {value !== "—" && unit && <span className="ml-0.5 text-[11px] text-muted-foreground">{unit}</span>}
    </div>
  );
}

/** Valor/cor/nota da coluna Produzido a partir da decisão de produção. */
function productionCell(
  status: ProductionItemStatus | null | undefined,
  produced?: number | null,
): { value: string; tone: QtyTone; strike?: boolean; note?: string; noteClass?: string } {
  switch (status) {
    case "PRONTO":
      return { value: fmtQty(produced), tone: "ok" };
    case "PARCIAL":
      return { value: fmtQty(produced), tone: "warn", note: "parcial", noteClass: "text-amber-700" };
    case "SEM_MERCADORIA":
      return { value: fmtQty(produced ?? 0), tone: "bad", note: "não produzido", noteClass: "text-destructive" };
    case "CANCELADO":
      return { value: fmtQty(produced ?? 0), tone: "muted", strike: true, note: "cancelado", noteClass: "text-muted-foreground" };
    default:
      // PENDENTE, EM_ANDAMENTO ou sem decisão de produção ainda.
      return { value: "—", tone: "muted" };
  }
}

/** Valor/cor da coluna Carregado a partir do status de expedição. */
function loadCell(
  status: "PENDENTE" | "CARREGADO" | "EM_FALTA",
  loaded: number | null,
): { value: string; tone: QtyTone } {
  if (status === "CARREGADO") return { value: fmtQty(loaded), tone: "ok" };
  if (status === "EM_FALTA") return { value: loaded != null ? fmtQty(loaded) : "0", tone: "warn" };
  return { value: "—", tone: "muted" };
}

function ExpeditionBadge({ status }: { status: "PENDENTE" | "CARREGADO" | "EM_FALTA" }) {
  if (status === "CARREGADO") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Carregado
      </span>
    );
  }
  if (status === "EM_FALTA") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700">
        <CircleSlash className="h-3 w-3" /> Em falta
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <Clock className="h-3 w-3" /> Pendente
    </span>
  );
}
